import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { Radio } from '@nx-frontend/shared/ui/radio';
import { getBorderClassNames } from '@nx-frontend/shared/themes';
import { PortalBorderType, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';

interface ChatbotFeedbackFormPopupProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onSubmit: (text: string) => void;
  feedbackBottomText?: string;
}

export function ChatbotFeedbackFormPopup({
  themeColors,
  themeStyle,
  onSubmit,
  feedbackBottomText,
}: ChatbotFeedbackFormPopupProps) {
  const [feedbackTheme, setFeedbackTheme] = useState('');
  const [inAnim, setInAnim] = useState(false);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const commentWrapperRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('features');

  const feedbackBottomTextPrepared = feedbackBottomText ?? t('ai-helper.all_feedback_is_reviewed');

  const otherThemeText = t('ai-helper.other');
  const feedbackThemes = [
    t('ai-helper.inaccurate_information'),
    t('ai-helper.didnt_help'),
    t('ai-helper.long_response_time'),
    t('ai-helper.answer_is_not_detailed'),
    otherThemeText,
  ];

  const allowSubmit = useMemo(() => {
    return feedbackTheme !== '';
  }, [feedbackTheme]);

  const onThemeChange = useCallback((fTheme: string) => {
    setFeedbackTheme(fTheme);
  }, []);

  const onFeedbackSubmit = useCallback(() => {
    const text = feedbackTheme === otherThemeText ? commentRef.current?.value || otherThemeText : feedbackTheme;
    onSubmit(text);
  }, [feedbackTheme, onSubmit, otherThemeText]);

  useEffect(() => {
    if (feedbackTheme === otherThemeText) {
      setInAnim(true);
    } else {
      setInAnim(false);
    }
  }, [feedbackTheme, otherThemeText]);

  return (
    <div className="flex flex-col gap-3 text-[13px]">
      <div className="flex flex-col gap-[18px]">
        {feedbackThemes.map((fTheme, index) => (
          <Radio
            id={`ai-feedback-theme-${index}`}
            checked={feedbackTheme === fTheme}
            value={fTheme}
            name="ai-feedback-theme"
            onChange={(checked) => checked && onThemeChange(fTheme)}
            label={fTheme}
          />
        ))}
      </div>
      <CSSTransition
        nodeRef={commentWrapperRef}
        in={inAnim}
        timeout={200}
        classNames={{
          appear: 'h-0',
          appearActive: 'transition-all duration-200 h-[88px]',
          enter: 'h-0',
          enterActive: 'transition-all duration-200 h-[88px]',
          enterDone: 'h-[88px]',
          exit: 'h-[88px]',
          exitActive: 'transition-all duration-200 !h-0',
          exitDone: 'h-0',
        }}
      >
        <div className="overflow-hidden h-0" ref={commentWrapperRef}>
          <textarea
            ref={commentRef}
            className={classNames(
              'w-[373px] h-[88px] rounded-lg resize-none outline-none px-3 py-[18px] h-0',
              getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE)
            )}
            placeholder={t('ai-helper.enter_your_comment_here')}
          ></textarea>
        </div>
      </CSSTransition>
      {feedbackBottomTextPrepared && <div className="text-neutral-500">{feedbackBottomTextPrepared}</div>}
      <div className="flex justify-end mt-5">
        <Button type={ButtonType.FLAT} disabled={!allowSubmit} onClick={onFeedbackSubmit}>
          {t('ai-helper.submit')}
        </Button>
      </div>
    </div>
  );
}
